import React, { useState } from 'react';
import { useSelector, connect } from 'react-redux';
import compose from 'lodash/flowRight';
import { InputAdornment, Drawer, Tab, Tabs } from '@material-ui/core';
import { Search, Close } from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import { HeadingText40, BodyTextMedium } from '@modernhealth/vital-components';

import { getActiveConversation } from 'src/selectors/chatSelectors';

import { store } from '../../config/Store';
import { colors } from '../../themes/colors';
import MHInput from '../MHInput';
import ChatResourceItem from './ChatResourceItem';
import ChatResourceInfoPanel from './ChatResourceInfoPanel';

const styles = () => ({
  popoverContainer: {
    height: 520,
    width: 400,
    display: 'flex',
    flexDirection: 'column',
  },
  headerWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 16px',
  },
  closeButton: {
    cursor: 'pointer',
  },
  searchInput: {
    margin: 10,
  },
  searchInputContainer: {
    margin: '12px 16px',
  },
  searchIcon: {
    color: colors.gray5,
  },
  tabs: {
    borderBottom: `1px solid ${colors.gray12}`,
  },
  tab: {
    minWidth: '132px',
  },
  listContainer: {
    width: 400,
    overflowY: 'auto',
  },
  listItem: {
    borderBottom: `1px solid ${colors.gray12}`,
  },
  listCardItem: {
    display: 'flex',
    padding: 10,
  },
  listItemTextContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  itemImage: {
    height: 44,
    width: 44,
    borderRadius: 3,
    marginRight: 12,
  },
  itemName: {
    fontFamily: 'Avenir-Heavy',
    fontSize: 14,
    color: colors.blue2,
  },
  itemType: {
    fontFamily: 'Avenir-Book',
    fontSize: 14,
    color: colors.gray5,
  },
  recommendedIconDescription: {
    display: 'flex',
    paddingLeft: '16px',
    alignItems: 'center',
  },
  recommendedIcon: {
    height: '20px',
    width: '20px',
  },
});

const ChatResourceDrawer = ({
  classes,
  anchorEl,
  isOpen,
  onClose,
  toggleResource,
  meditations,
  programs,
  companyAdditionalBenefits,
  showResourceInfoPanel,
  toggleShowResourceInfoPanel,
  setSelectedItem,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const itemsArray = [programs, meditations, companyAdditionalBenefits];
  const items = itemsArray[currentTab];
  const activeConversation = useSelector(getActiveConversation);

  const onSearchTermChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleTabChanage = (event, newTabNumber) => {
    setCurrentTab(newTabNumber);
  };

  const filterItem = (item) => item.name.toLowerCase().includes(searchTerm.toLowerCase());

  const renderItem = (item) => (
    <ChatResourceItem
      key={item.uniqueKey}
      item={item}
      toggleResource={toggleResource}
      fromDrawer
      toggleShowResourceInfoPanel={toggleShowResourceInfoPanel}
      setSelectedItem={setSelectedItem}
    />
  );

  const renderContent = (
    <>
      <div>
        <div className={classes.headerWrapper}>
          <HeadingText40 marginTop={16} marginBottom={0}>
            Resources
          </HeadingText40>
          <Close
            className={classes.closeButton}
            onClick={() => {
              onClose();
              showResourceInfoPanel && toggleShowResourceInfoPanel();
            }}
          />
        </div>

        <div className={classes.recommendedIconDescription}>
          <img
            className={classes.recommendedIcon}
            src={require('../../assets/images/star.svg').default}
            alt="recommended"
          />
          <BodyTextMedium
            marginLeft={8}
            fontColor="gray55"
          >{`Related to ${activeConversation.member.firstName}’s topics of focus`}</BodyTextMedium>
        </div>

        <div className={classes.searchInputContainer}>
          <MHInput
            value={searchTerm}
            onChange={onSearchTermChange}
            classes={classes.searchInput}
            placeholder="Search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search className={classes.searchIcon} />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <Tabs
          value={currentTab}
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabs}
          onChange={handleTabChanage}
        >
          <Tab className={classes.tab} label="Programs" />
          <Tab className={classes.tab} label="Meditations" />
          {companyAdditionalBenefits.length > 0 && <Tab className={classes.tab} label="Benefits" />}
        </Tabs>
      </div>
      <div className={classes.listContainer}>{items.filter(filterItem).map(renderItem)}</div>
      {showResourceInfoPanel && <ChatResourceInfoPanel />}
    </>
  );

  return (
    <Drawer
      id="resource-drawer"
      open={isOpen}
      anchor="right"
      onClose={() => {
        onClose();
        showResourceInfoPanel && toggleShowResourceInfoPanel();
      }}
      BackdropProps={{ invisible: true }}
      elevation={6}
    >
      {renderContent}
    </Drawer>
  );
};

const mapState = (state) => ({
  meditations: store.select.resources.decoratedMeditations(state),
  programs: store.select.resources.decoratedPrograms(state),
  companyAdditionalBenefits: store.select.resources.decoratedBenefits(state),
  showResourceInfoPanel: state.resources.showResourceInfoPanel,
});

const mapDispatch = (dispatch) => ({
  toggleResource: dispatch.resources.toggleResource,
  toggleShowResourceInfoPanel: dispatch.resources.toogleShowResourceInfoPanel,
  setSelectedItem: dispatch.resources.setSelectedItem,
});

export default compose(withStyles(styles), connect(mapState, mapDispatch))(ChatResourceDrawer);
